// WishTimelinePanel — milestone timeline (W35 §3.4).
//
// Per docs/spec/wishlist/W35.2-SUBRESOURCE-UI-POLISH.md §1.3:
// - per-row: dot (filled = completed) + milestone text + target_date +
//   ✓ if completed + complete button + delete
// - add row: "+ 新增里程碑" trigger
// - empty state: "還沒有里程碑。新增第一個進度節點。"
//
// Sized for the AGENTS.md §3 #10 sub-component budget (~6KB).

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarCheck, Circle, CircleCheck, Plus, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ErrorState } from "@/components/primitives/ErrorState";
import { useToast } from "@/components/ui/toast";
import {
  completeTimelineItem,
  createTimelineItem,
  deleteTimelineItem,
  listTimeline,
  type WishTimelineItemOut,
} from "@/lib/api/wishlist";
import { queryKeys } from "@/lib/queries/keys";
import { cn } from "@/lib/utils/cn";

export interface WishTimelinePanelProps {
  wishId: string;
  className?: string;
}

export function WishTimelinePanel({
  wishId,
  className,
}: WishTimelinePanelProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ milestone: "", target_date: "" });

  const query = useQuery({
    queryKey: ["wishes", "detail", wishId, "timeline"],
    queryFn: () => listTimeline(wishId),
    enabled: Boolean(wishId),
    staleTime: 30_000,
  });

  const create = useMutation({
    mutationFn: () =>
      createTimelineItem(wishId, {
        milestone: form.milestone.trim(),
        target_date: form.target_date,
      }),
    onSuccess: () => {
      setAdding(false);
      setForm({ milestone: "", target_date: "" });
      void queryClient.invalidateQueries({
        queryKey: ["wishes", "detail", wishId, "timeline"],
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
    onError: () =>
      toast.push({ message: "新增里程碑失敗", variant: "danger" }),
  });

  const complete = useMutation({
    mutationFn: (id: string) => completeTimelineItem(wishId, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["wishes", "detail", wishId, "timeline"],
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => deleteTimelineItem(wishId, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["wishes", "detail", wishId, "timeline"],
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });

  const items: WishTimelineItemOut[] = (query.data ?? []).slice().sort(
    (a, b) => a.position - b.position,
  );

  return (
    <Card
      data-component="WishTimelinePanel"
      data-testid="wish-timeline-panel"
      className={cn("p-s-4", className)}
    >
      <header className="flex items-center justify-between gap-2 mb-s-3">
        <h3 className="font-display text-h3 text-ink-1 flex items-center gap-2">
          <CalendarCheck className="h-4 w-4" aria-hidden />
          心願里程碑
          <span className="text-meta text-ink-3 font-normal">{items.length}</span>
        </h3>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setAdding((v) => !v)}
          data-testid="wish-timeline-add-toggle"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          {adding ? "取消" : "新增里程碑"}
        </Button>
      </header>
      {adding ? (
        <form
          data-testid="wish-timeline-add-form"
          className="flex flex-col gap-s-2 mb-s-3 p-s-3 bg-paper-deep rounded-md"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <input
            type="text"
            value={form.milestone}
            onChange={(e) =>
              setForm((f) => ({ ...f, milestone: e.target.value }))
            }
            placeholder="里程碑 (例: 付首期)"
            maxLength={200}
            required
            data-testid="wish-timeline-add-milestone"
            className="w-full px-s-3 py-s-2 rounded-md border border-hairline bg-paper-elevated text-body-sm text-ink-1"
          />
          <input
            type="date"
            value={form.target_date}
            onChange={(e) =>
              setForm((f) => ({ ...f, target_date: e.target.value }))
            }
            required
            data-testid="wish-timeline-add-date"
            className="w-full px-s-3 py-s-2 rounded-md border border-hairline bg-paper-elevated text-body-sm text-ink-1"
          />
          <Button
            type="submit"
            disabled={
              create.isPending ||
              !form.milestone.trim() ||
              !form.target_date
            }
            data-testid="wish-timeline-add-submit"
            className="press min-h-11 px-s-4 rounded-full bg-ink-1 text-paper"
          >
            確認新增
          </Button>
        </form>
      ) : null}
      {query.isLoading ? (
        <p className="text-meta text-ink-3">載入中…</p>
      ) : query.isError ? (
        // W54.0 B1 — a failed read is not 「還沒有里程碑」. The empty state
        // says there is no progress yet; the error state says we could not
        // find out. Only the first one is worth acting on.
        <ErrorState onRetry={() => void query.refetch()} />
      ) : items.length === 0 ? (
        <p className="text-meta text-ink-3">
          還沒有里程碑。新增第一個進度節點。
        </p>
      ) : (
        <ol className="flex flex-col gap-s-2">
          {items.map((it) => {
            const done = it.completed_at !== null;
            return (
              <li
                key={it.id}
                data-component="WishTimelineRow"
                data-testid={`wish-timeline-row-${it.id}`}
                data-completed={done ? "true" : "false"}
                className="flex items-center gap-2"
              >
                {done ? (
                  <CircleCheck
                    className="h-4 w-4 shrink-0 text-ink-1"
                    aria-hidden
                  />
                ) : (
                  <Circle className="h-4 w-4 shrink-0 text-ink-3" aria-hidden />
                )}
                <div className="flex-1 min-w-0">
                  <p
                    className={cn(
                      "text-body-sm",
                      done
                        ? "text-ink-3 line-through"
                        : "text-ink-1",
                    )}
                  >
                    {it.milestone}
                  </p>
                  <p className="text-meta text-ink-3 tabular-nums">
                    目標 {it.target_date}
                  </p>
                </div>
                {!done ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => complete.mutate(it.id)}
                    disabled={complete.isPending}
                    data-testid={`wish-timeline-row-complete-${it.id}`}
                    className="press min-h-11 min-w-11 px-s-3 rounded-full"
                  >
                    完成
                  </Button>
                ) : null}
                <button
                  type="button"
                  onClick={() => del.mutate(it.id)}
                  disabled={del.isPending}
                  data-testid={`wish-timeline-row-delete-${it.id}`}
                  aria-label="刪除里程碑"
                  className="press shrink-0 min-h-11 min-w-11 flex items-center justify-center rounded-md text-ink-3 hover:bg-paper-deep disabled:opacity-50"
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden />
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}